import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { SvgIcon } from 'ui/SvgIcon';
import ChevronDownIcon from 'ui/Icons/tabler/chevron-down.component.svg';
import LoaderIcon from 'ui/Icons/tabler/loader.component.svg';
import { VerticalLine } from 'ui/VerticalLine';
import { theme } from '../../../tailwind.config';
import FluidButton from 'ui/FluidButton';

export interface IExportDropdownOption {
  label: string;
  onClick: () => void;
}

export interface IExportDropdownButtonProps {
  options: IExportDropdownOption[];
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

export const ExportDropdownButton: React.FC<IExportDropdownButtonProps> = ({
  options,
  isLoading = false,
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const buttonClassName = classNames(
    'flex justify-center items-center select-none font-pt-sans text-sm font-bold leading-xs tracking-wider outline-none rounded border border-solid min-h-35px max-h-35px px-3 bg-white-true',
    {
      'text-brown-100 border-brown-100 hover:bg-brown-20 cursor-pointer': !disabled,
      'border-gray-60 text-gray-40 cursor-not-allowed': disabled,
    }
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleOptionClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  const handleButtonClick = () => {
    if (!disabled && !isLoading) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div ref={dropdownRef} className={classNames('relative', className)}>
      <FluidButton
        type="secondary"
        onClick={handleButtonClick}
        disabled={disabled}
        className={buttonClassName}
      >
        {isLoading ? (
          <SvgIcon
            IconComponent={LoaderIcon}
            className="w-5 h-5 fill-transparent stroke-current stroke-2 animate-spin"
          />
        ) : (
          <span className="flex items-center gap-2">
            <span>Export</span>
            <VerticalLine height="20px" color={disabled ? theme.colors['gray-20'] : theme.colors['brown-40']} />
            <span
              className={classNames('transition-transform', {
                'rotate-180': isOpen,
              })}
            >
              <SvgIcon IconComponent={ChevronDownIcon} className="relative top-[2px] w-[18px] h-[18px] fill-transparent stroke-current stroke-2" />
            </span>
          </span>
        )}
      </FluidButton>
      {isOpen && !isLoading && (
        <div className="absolute top-full right-0 mt-1 z-10 bg-white border border-solid border-gray-40 rounded shadow-lg min-w-full">
          <div className="export-dropdown-button-options flex flex-col pl-[10px] pr-[5px] py-[5px] w-[165px]">
            {options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option.onClick)}
                className={classNames(
                  'm-0 p-0 px-3 py-2 bg-transparent font-pt-sans text-sm text-left hover:bg-gray-10 cursor-pointer box-border text-black'
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

